import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SupplierOrder } from '../types';
import { StatusBadge, SectionDot } from './OrderStatusBadge';
import styles from './OrdersPage.module.css';

const COLORS = {
  navy: '#0E1333',
  amber: '#F59E0B',
  amberLight: '#FEF3C7',
  amberBorder: '#FDE68A',
  blue: '#3B82F6',
  blueLight: '#DBEAFE',
  blueBorder: '#93C5FD',
  green: '#10B981',
  greenLight: '#D1FAE5',
  greenBorder: '#6EE7B7',
  muted: '#64748b',
  border: '#e0e3ef',
};

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const datePart = date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
  const timePart = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${datePart}, ${timePart}`;
}

function OrderCardDraft({ order, isSelected, onClick }: {
  order: SupplierOrder;
  isSelected: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <div
      onClick={onClick}
      className={styles.orderCard}
      style={{
        border: isSelected
          ? `2px dashed ${COLORS.amber}`
          : undefined,
      }}
    >
      <div className={styles.orderCardHeader}>
        <span className={styles.orderCardName}>{order.supplier_name}</span>
        <StatusBadge label="Draft" color={COLORS.amber} bgColor={COLORS.amberLight} />
      </div>
      <div className={styles.orderCardSub}>{order.item_count} items</div>
      {order.source === 'inventory_auto' && (
        <div className={styles.orderCardAutoTag}>
          Auto-generated from inventory
        </div>
      )}
    </div>
  );
}

function OrderCardSent({ order, isSelected, onClick }: {
  order: SupplierOrder;
  isSelected: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <div
      onClick={onClick}
      className={styles.orderCard}
      style={{
        border: isSelected
          ? `2px solid ${COLORS.blue}`
          : undefined,
      }}
    >
      <div className={styles.orderCardHeader}>
        <span className={styles.orderCardName}>{order.supplier_name}</span>
        <StatusBadge label="In Transit" color={COLORS.blue} bgColor={COLORS.blueLight} />
      </div>
      <div className={styles.orderCardSub}>
        Sent {formatDateTime(order.sent_at)}
      </div>
      {order.expected_delivery && (
        <div className={styles.orderCardExpected}>
          Expected {formatDate(order.expected_delivery)}
        </div>
      )}
    </div>
  );
}

function OrderCardDelivered({ order, isSelected, onClick }: {
  order: SupplierOrder;
  isSelected: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <div
      onClick={onClick}
      className={styles.orderCard}
      style={{
        border: isSelected
          ? `2px solid ${COLORS.green}`
          : undefined,
      }}
    >
      <div className={styles.orderCardHeader}>
        <span className={styles.orderCardName}>{order.supplier_name}</span>
        <StatusBadge label="Delivered" color={COLORS.green} bgColor={COLORS.greenLight} />
      </div>
      <div className={styles.orderCardReady}>
        {order.item_count} items ready for check-in
      </div>
    </div>
  );
}

interface OrderListViewProps {
  draftOrders: SupplierOrder[];
  sentOrders: SupplierOrder[];
  deliveredOrders: SupplierOrder[];
  selectedOrderId: number | null;
  loadingOrders: boolean;
  totalOrders: number;
  onSelectOrder: (orderId: number) => void;
}

export function OrderListView({
  draftOrders,
  sentOrders,
  deliveredOrders,
  selectedOrderId,
  loadingOrders,
  totalOrders,
  onSelectOrder,
}: OrderListViewProps): React.JSX.Element {
  return (
    <div className={styles.orderListContainer}>
      {draftOrders.length > 0 && (
        <div>
          <div className={styles.sectionHeader}>
            <SectionDot color={COLORS.amber} />
            <span className={styles.sectionLabel} style={{ color: COLORS.amber }}>
              Drafts ({draftOrders.length})
            </span>
          </div>
          <div className={styles.sectionCards}>
            {draftOrders.map((order) => (
              <OrderCardDraft
                key={order.id}
                order={order}
                isSelected={selectedOrderId === order.id}
                onClick={() => onSelectOrder(order.id)}
              />
            ))}
          </div>
        </div>
      )}

      {sentOrders.length > 0 && (
        <div>
          <div className={styles.sectionHeader}>
            <SectionDot color={COLORS.blue} />
            <span className={styles.sectionLabel} style={{ color: COLORS.blue }}>
              Sent ({sentOrders.length})
            </span>
          </div>
          <div className={styles.sectionCards}>
            {sentOrders.map((order) => (
              <OrderCardSent
                key={order.id}
                order={order}
                isSelected={selectedOrderId === order.id}
                onClick={() => onSelectOrder(order.id)}
              />
            ))}
          </div>
        </div>
      )}

      {deliveredOrders.length > 0 && (
        <div>
          <div className={styles.sectionHeader}>
            <SectionDot color={COLORS.green} pulsing />
            <span className={styles.sectionLabel} style={{ color: COLORS.green }}>
              Ready to Receive ({deliveredOrders.length})
            </span>
          </div>
          <div className={styles.sectionCards}>
            {deliveredOrders.map((order) => (
              <OrderCardDelivered
                key={order.id}
                order={order}
                isSelected={selectedOrderId === order.id}
                onClick={() => onSelectOrder(order.id)}
              />
            ))}
          </div>
        </div>
      )}

      {!loadingOrders && totalOrders === 0 && (
        <div className={styles.emptyState}>No orders yet</div>
      )}

      {loadingOrders && (
        <div className={styles.emptyState}>Loading orders...</div>
      )}
    </div>
  );
}
